import { LockOpen, NotInterested, Send } from "@material-ui/icons";
import {
  AppBarButton,
  AppBarConfirmButton,
  AppBarSaveButton,
  AppBarValidationIndicator,
} from "@ofqual/portal-components/components/SecondaryAppBar";
import { useAuth } from "@ofqual/portal-core/core/hooks";
import {
  getLookupTracker,
  modelToJsonPatchDocument,
} from "@ofqual/portal-core/core/utils/modelHelpers";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Prompt } from "react-router-dom";

import {
  defaultValues,
  requiredOfqualFields,
  SocItem,
  SocStatus,
} from "../../domain/SocItem";
import {
  closeSoc,
  enableResubmission,
  reopenSoc,
  updatingItem,
} from "../../redux/actions";
import { authSelector } from "../../redux/selectors";
import { NonNullableItemState } from "../../redux/state";
import { ModuleAppBar } from "../ModuleAppBar";
import { SectionProps } from "../sections/SectionProps";
import { ItemDetails } from "./ItemDetails";
import { ItemDetailsOfqual } from "./ItemDetailsOfqual";
import { ItemTabs } from "./ItemTabs";
import { GuidanceDrawer } from "@ofqual/portal-components/components/Drawer";
import { guidanceService } from "../../services";
import { SendDialog } from "./SendDialog";
import { UserType } from "../../redux/types";
import { CloseDialog } from "./activityFeed/dialogs/CloseDialog";

export const ItemForm: React.FC<{
  state: NonNullableItemState;
}> = ({ state: { item, status, lastCrudResult } }) => {
  const [open, setOpen] = useState(false);
  const toggleOpen = (isOpen: boolean) => setOpen(isOpen);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { userType, token } = useAuth(authSelector);

  const defaults =
    item.status === SocStatus.Draft && userType !== UserType.AO
      ? defaultValues
      : item;
  const {
    handleSubmit,
    control,
    formState,
    errors,
    setValue,
    setError,
    clearErrors,
    getValues,
    reset,
    watch,
  } = useForm<SocItem>({
    defaultValues: defaults,
    shouldUnregister: false,
  });

  useEffect(() => {
    // on save (detected by a new item in redux) we need to clear dirtyFields, so reset is required
    reset(defaults);
  }, [reset, defaults]);

  const dispatch = useDispatch();

  // this is important for dirtyFields to be deconstructed/accessed prior to the onSubmit process
  const { dirtyFields } = formState;
  const lookupTracker = useMemo(() => getLookupTracker(), []);
  const aoDisabled =
    userType === UserType.AO &&
    item.status !== SocStatus.Draft &&
    item.status !== SocStatus.New;
  const ofqualDisabled =
    userType === UserType.Ofqual && item.status === SocStatus.Closed;
  const disabled = aoDisabled || ofqualDisabled;

  const onSubmit = () => {
    const data: SocItem = getValues();
    const currencyKeys = [
      "currentRevenueIncome",
      "currentExpenditure",
      "currentCashLiquidReserves",
      "currentNumberOfEmployeesAndContractors",
      "forecastRevenueIncome",
      "forecastExpenditure",
      "forecastCashLiquidReserves",
      "monthlyFixedCost",
      "monthlyCoverage",
      "forecastNumberOfEmployees",
      "forecastNumberOfContractors",
      "accountingYearCurrentNumberOfEmployees",
      "accountingYearCurrentNumberOfContractors",
    ];

    const surplusKeys = ["currentProfitSurplus", "forecastProfitSurplus"];

    for (const key of currencyKeys) {
      const socKey = key as keyof SocItem;
      if (data[socKey] !== null) {
        if (
          !/^\d{1,15}$|(?=^.{1,15}$)^\d+\.\d{0,2}$/.test(
            data[socKey]!.toString()
          )
        ) {
          setError(socKey, {
            type: "pattern",
            message: "Value does not match the format of currency",
          });
          return;
        }
      }
    }

    for (const key of surplusKeys) {
      const socKey = key as keyof SocItem;
      if (data[socKey] !== null) {
        if (
          !/^-?\d{1,15}$|(?=^.{1,15}$)^-?\d+\.\d{0,2}$/.test(
            data[socKey]!.toString()
          )
        ) {
          setError(socKey, {
            type: "pattern",
            message: "Value does not match the format of currency",
          });
          return;
        }
      }
    }

    const changedFields = Object.keys(dirtyFields) as (keyof SocItem)[];
    const newModel: SocItem = getValues();
    let document = modelToJsonPatchDocument(newModel, changedFields);

    dispatch(updatingItem(item.id, document));
  };

  const onClick = () => setIsDialogOpen(true);

  const onCloseSOC = () => {
    const closeText = getValues("closeReason");
    if (closeText?.length === 0) {
      setError("closeReason", { type: "required" });
      return;
    }

    const changedFields = Object.keys(dirtyFields) as (keyof SocItem)[];
    const newModel: SocItem = getValues();
    const reasonReq =
      newModel.agreeCurrentCompliant === false ||
      newModel.agreeFutureCompliant === false;
    const reqFields = [...requiredOfqualFields];
    if (reasonReq) reqFields.push("complianceDisagreement");

    reqFields.forEach((field) => {
      if (newModel[field] === null || newModel[field] === undefined) {
        setError(field, {
          type: "required",
        });
      }
    });
    if (Object.keys(errors).length === 0) {
      let document = modelToJsonPatchDocument(newModel, changedFields);
      dispatch(closeSoc(item.id, document));
    }
  };

  const onReopenSoc = (id: string) => dispatch(reopenSoc(id));
  const onEnableResubmission = (id: string) => dispatch(enableResubmission(id));

  const sectionProps: SectionProps<SocItem> = {
    isCreateMode: false,
    disabled,
    user: { userType, token },
    control,
    errors,
    formState,
    item,
    clearErrors,
    setError,
    setValue: (name, value) => setValue(name, value),
    getValues,
    registerCustomError: (name: string, isError: boolean) => {
      const isCurrentlyARegisteredError = Object.keys(errors).includes(name);
      if (isError && !isCurrentlyARegisteredError) {
        setError(name, {});
      }
      if (!isError && isCurrentlyARegisteredError) {
        clearErrors(name);
      }
    },
    lookupTracker,
    watch,
  };

  const mappedGuidanceService = useMemo(
    () => ({
      get: () => {
        if (userType === UserType.Ofqual) {
          return guidanceService.get(
            { module: "soc", key: "edit-soc-ofqual" },
            { token }
          );
        } else if (userType === UserType.CCEA) {
          return guidanceService.get(
            { module: "soc", key: "edit-soc-ccea" },
            { token }
          );
        } else {
          return guidanceService.get(
            { module: "soc", key: "edit-soc-ao" },
            { token }
          );
        }
      },
    }),
    [token, userType]
  );

  const canSend =
    userType === UserType.AO &&
    (item.status === SocStatus.Draft || item.status === SocStatus.New);

  const canClose =
    userType === UserType.Ofqual && item.status !== SocStatus.Closed;

  const canReopen =
    userType === UserType.Ofqual && item.status === SocStatus.Closed;

  const canEnableResubmission =
    userType === UserType.Ofqual && item.status === SocStatus.Submitted;

  const isMagicLinkUser = userType === UserType.Magic;

  const watchCloseReason = sectionProps.watch("closeReason");

  const handleCloseDialogCleanup = () => {
    setValue("closeReason", "");
    clearErrors("closeReason");
  };

  return (
    <>
      <Prompt
        message="There are unsaved changes in the form.  Are you sure you want to navigate away from this page and lose these changes?"
        when={Object.keys(formState.dirtyFields).length > 0}
      />
      <ModuleAppBar breadcrumbs={[{ label: `${item.caseTitle}` }]}>
        <AppBarValidationIndicator errors={errors} />
        {canClose ? (
          <AppBarConfirmButton
            dataTest="close-soc"
            tooltip="Close SoC"
            Icon={NotInterested}
            onClick={onCloseSOC}
            onClose={handleCloseDialogCleanup}
            shouldNotClose={
              watchCloseReason === undefined || watchCloseReason === null
                ? true
                : watchCloseReason.length < 1 && true
            }
            text="Close"
          >
            <CloseDialog {...sectionProps} />
          </AppBarConfirmButton>
        ) : null}
        {canReopen ? (
          <AppBarConfirmButton
            dataTest="reopen-soc"
            Icon={LockOpen}
            onClick={() => onReopenSoc(item.id)}
            text="Re-open"
            tooltip="Re-open SoC"
          >
            Are you sure you want to re-open this SoC?
          </AppBarConfirmButton>
        ) : null}

        {canEnableResubmission ? (
          <AppBarConfirmButton
            dataTest="allow-ao-resubmission"
            Icon={LockOpen}
            onClick={() => onEnableResubmission(item.id)}
            text="Allow Resubmission"
            tooltip="Allow AO resubmission"
          >
            Are you sure you want to allow the AO to resubmit this SoC?
          </AppBarConfirmButton>
        ) : null}

        {!isMagicLinkUser ? (
          <AppBarSaveButton
            onClick={onSubmit}
            isSubmitting={status === "UPDATING"}
            isSubmitted={lastCrudResult.isSuccess}
            dataTest="save-soc-button"
            disabled={disabled}
          />
        ) : null}

        {canSend ? (
          <>
            <AppBarButton
              onClick={handleSubmit(onClick)}
              color="primary"
              text="Send"
              Icon={Send}
              data-test="send-button"
            />
            <SendDialog
              dirtyFields={dirtyFields}
              newModel={getValues()}
              soc={item}
              isOpen={isDialogOpen}
              onClose={() => setIsDialogOpen(false)}
            />
          </>
        ) : (
          false
        )}

        {!isMagicLinkUser ? (
          <GuidanceDrawer
            open={open}
            handleDrawerToggle={toggleOpen}
            service={mappedGuidanceService}
          />
        ) : null}
      </ModuleAppBar>
      <ItemTabs indicatorProps={sectionProps}>
        {(activeTab) =>
          activeTab === "ofqual" ? (
            <ItemDetailsOfqual sectionProps={sectionProps} />
          ) : (
            <ItemDetails sectionProps={sectionProps} />
          )
        }
      </ItemTabs>
    </>
  );
};
